/* eslint-disable sonarjs/cognitive-complexity, complexity -- bidirectional API<->grid field mapping is inherently long and branchy; splitting it would fragment the 1:1 field correspondence without reducing real complexity */
/**
 * Transform functions for Long-Term Disability (LTD) benefits
 *
 * Handles bi-directional transformation between:
 * - API format (nested objects) - what the backend expects/returns
 * - Grid format (flat fields) - what the AgGrid component uses
 *
 * Uses shared transform builders for common patterns.
 */

import {
  BenefitDurationTypeEnum,
  DefinitionOfEarningsEnum,
  RfpOwnOccupationPeriodType,
} from "@platform/openapi-types";

import {
  buildConditionalTypeAmount,
  cleanupNotDisclosedBenefits,
  isValidAmount,
  isValidCount,
  shouldIncludeBenefitInApi,
  transformPreExistingConditionFromApi,
  transformPreExistingConditionToApi,
  transformTypeAmountToApi,
  transformTypeMultipleAmountsFromApi,
  transformTypeMultipleAmountsToApi,
} from "../shared/builders";

import { LTD_VALID_FIELDS } from "./validFields";

import type {
  LtdBenefitApi,
  LtdBenefitGrid,
  LtdExtendedBenefitApi,
} from "./types";

const FIELDS = {
  PERCENTAGE: "percentage",
  DAYS: "days",
};

// Types that carry an averageMonth (variable compensation needing an averaging period)
const DOE_TYPES_WITH_AVERAGE_MONTH = new Set<string>([
  DefinitionOfEarningsEnum.salaryPlusCommissions,
  DefinitionOfEarningsEnum.salaryPlusBonus,
  DefinitionOfEarningsEnum.salaryPlusBonusPlusCommissions,
]);

const shouldIncludeBenefitAmountMinimum = (
  benefitAmountType: LtdBenefitGrid["benefitAmountType"],
  benefitAmountAmount: LtdBenefitGrid["benefitAmountAmount"],
): boolean => {
  if (benefitAmountType === "percentageOfSalary") return false;
  // flatAmount requires amount to be a valid payload; only include minimum when amount is also present
  if (benefitAmountType === "flatAmount")
    return isValidAmount(benefitAmountAmount);
  return true;
};

/**
 * Transforms LTD benefits from grid format to API format
 *
 * @param gridBenefits - Benefits in flat grid format
 * @returns Benefits in nested API format with objectType and valid fields filtered
 */
export const transformLtdBenefitsToApi = (
  gridBenefits: Partial<LtdBenefitGrid>,
): LtdBenefitApi => {
  const transformed: Partial<LtdBenefitApi> = {};
  const benefitAmountMinimum = shouldIncludeBenefitAmountMinimum(
    gridBenefits.benefitAmountType,
    gridBenefits.benefitAmountAmount,
  )
    ? gridBenefits.benefitAmountMinimum
    : undefined;

  // Simple text fields
  if (gridBenefits.currentCarrierName)
    transformed.currentCarrierName = gridBenefits.currentCarrierName;
  if (gridBenefits.benefitClassAvailability)
    transformed.benefitClassAvailability =
      gridBenefits.benefitClassAvailability;

  // Transform BenefitAmount (type + amount + minimum) - using builder
  if (
    shouldIncludeBenefitInApi(gridBenefits.benefitAmountType) &&
    (isValidAmount(gridBenefits.benefitAmountAmount) ||
      isValidAmount(benefitAmountMinimum))
  ) {
    transformed.benefitAmount = transformTypeMultipleAmountsToApi(
      gridBenefits.benefitAmountType,
      {
        amount: gridBenefits.benefitAmountAmount,
        minimum: benefitAmountMinimum,
      },
    ) as unknown as LtdBenefitApi["benefitAmount"];
  }

  // Transform MaximumAmount (type + dollarAmount + percent) - using builder
  if (shouldIncludeBenefitInApi(gridBenefits.maximumAmountType)) {
    transformed.maximumAmount = transformTypeMultipleAmountsToApi(
      gridBenefits.maximumAmountType,
      {
        dollarAmount: gridBenefits.maximumAmountDollarAmount,
        percent: gridBenefits.maximumAmountPercent,
      },
    ) as unknown as LtdBenefitApi["maximumAmount"];
  }

  // Transform GuaranteedIssue (type + amount)
  transformed.guaranteedIssue = transformTypeAmountToApi(
    gridBenefits.guaranteedIssueType,
    gridBenefits.guaranteedIssueAmount,
    ["dollarAmount"],
  ) as LtdBenefitApi["guaranteedIssue"] | undefined;

  // Transform OwnOccupationPeriod (type + amount)
  // amount required only for yearsOwnOccupation/monthsOwnOccupation; ownOccupationOnly is type-only
  if (shouldIncludeBenefitInApi(gridBenefits.ownOccupationPeriodType)) {
    const ownOccupationType = gridBenefits.ownOccupationPeriodType;
    const amountRequired =
      ownOccupationType === RfpOwnOccupationPeriodType.yearsOwnOccupation ||
      ownOccupationType === RfpOwnOccupationPeriodType.monthsOwnOccupation;
    const result = buildConditionalTypeAmount(
      ownOccupationType,
      gridBenefits.ownOccupationPeriodAmount,
      "amount",
      amountRequired,
    );
    if (result) transformed.ownOccupationPeriod = result;
  }

  // Simple field mappings
  if (shouldIncludeBenefitInApi(gridBenefits.definitionOfDisability)) {
    transformed.definitionOfDisability = gridBenefits.definitionOfDisability;
  }

  // Transform GainfulEarningsTestOwnOccupation (type + amount)
  transformed.gainfulEarningsTestOwnOccupation = transformTypeAmountToApi(
    gridBenefits.gainfulEarningsTestOwnOccupationType,
    gridBenefits.gainfulEarningsTestOwnOccupationAmount,
    [FIELDS.PERCENTAGE],
  ) as LtdBenefitApi["gainfulEarningsTestOwnOccupation"] | undefined;

  // Transform GainfulEarningsTestAnyOccupation (type + amount)
  transformed.gainfulEarningsTestAnyOccupation = transformTypeAmountToApi(
    gridBenefits.gainfulEarningsTestAnyOccupationType,
    gridBenefits.gainfulEarningsTestAnyOccupationAmount,
    [FIELDS.PERCENTAGE],
  ) as LtdBenefitApi["gainfulEarningsTestAnyOccupation"] | undefined;

  // Transform EliminationPeriod (type + days)
  transformed.eliminationPeriod = transformTypeAmountToApi(
    gridBenefits.eliminationPeriodType,
    gridBenefits.eliminationPeriodDays,
    [FIELDS.DAYS],
  ) as LtdBenefitApi["eliminationPeriod"] | undefined;

  // Transform BenefitDuration (type + amount)
  // amount required for yearDuration; optional for lesserOfRbdOrNumberOfYears and laterOfAgeOrSsnra
  // (the latter carries an age). Other types (toAge65, toAge67, ssnra, etc.) are type-only; stale
  // amount must not be sent.
  if (shouldIncludeBenefitInApi(gridBenefits.benefitDurationType)) {
    const benefitDurationType = gridBenefits.benefitDurationType;
    const amountRequired =
      benefitDurationType === BenefitDurationTypeEnum.yearDuration;
    const amountSupported =
      amountRequired ||
      benefitDurationType ===
        BenefitDurationTypeEnum.lesserOfRbdOrNumberOfYears ||
      benefitDurationType === BenefitDurationTypeEnum.laterOfAgeOrSsnra;
    const result = buildConditionalTypeAmount(
      benefitDurationType,
      gridBenefits.benefitDurationAmount,
      "amount",
      amountRequired,
      amountSupported,
    );
    if (result) transformed.benefitDuration = result;
  }

  // Transform DefinitionOfEarnings (type + averageMonth)
  // averageMonth is optional and only sent for variable-compensation types; fixed-point types (salary, k1, etc.) are type-only
  if (shouldIncludeBenefitInApi(gridBenefits.definitionOfEarningsType)) {
    const supportsAverageMonth = DOE_TYPES_WITH_AVERAGE_MONTH.has(
      gridBenefits.definitionOfEarningsType ?? "",
    );
    const result = buildConditionalTypeAmount(
      gridBenefits.definitionOfEarningsType,
      gridBenefits.definitionOfEarningsAverageMonths,
      "averageMonth",
      false,
      supportsAverageMonth,
    );
    if (result) transformed.definitionOfEarnings = result;
  }

  // Transform MentalIllness (type + timeframe)
  if (shouldIncludeBenefitInApi(gridBenefits.mentalIllnessType)) {
    transformed.mentalIllness = {
      type: gridBenefits.mentalIllnessType,
    };
    // Only include timeframe for types that require it (years/months)
    if (
      (gridBenefits.mentalIllnessType === "years" ||
        gridBenefits.mentalIllnessType === "months") &&
      isValidCount(gridBenefits.mentalIllnessAmount)
    ) {
      transformed.mentalIllness.timeframe = gridBenefits.mentalIllnessAmount;
    }
  }

  // Transform SubstanceAbuse (type + timeframe)
  if (shouldIncludeBenefitInApi(gridBenefits.substanceAbuseType)) {
    transformed.substanceAbuse = {
      type: gridBenefits.substanceAbuseType,
    };
    // Only include timeframe for types that require it (years/months)
    if (
      (gridBenefits.substanceAbuseType === "years" ||
        gridBenefits.substanceAbuseType === "months") &&
      isValidCount(gridBenefits.substanceAbuseAmount)
    ) {
      transformed.substanceAbuse.timeframe = gridBenefits.substanceAbuseAmount;
    }
  }

  // Transform PreExistingCondition (type + monthsDetail string) - using builder
  if (shouldIncludeBenefitInApi(gridBenefits.preExistingConditionType)) {
    transformed.preExistingCondition = transformPreExistingConditionToApi(
      gridBenefits.preExistingConditionType,
      gridBenefits.preExistingConditionMonthsDetail,
      "none",
      "01/01",
    ) as unknown as LtdBenefitApi["preExistingCondition"];
  }

  // Simple enum fields
  if (shouldIncludeBenefitInApi(gridBenefits.rehab))
    transformed.rehab = gridBenefits.rehab;
  if (shouldIncludeBenefitInApi(gridBenefits.cola))
    transformed.cola = gridBenefits.cola;
  if (shouldIncludeBenefitInApi(gridBenefits.conversion))
    transformed.conversion = gridBenefits.conversion;
  if (shouldIncludeBenefitInApi(gridBenefits.waiverOfPremium))
    transformed.waiverOfPremium = gridBenefits.waiverOfPremium;
  if (shouldIncludeBenefitInApi(gridBenefits.employerContribution))
    transformed.employerContribution = gridBenefits.employerContribution;
  if (shouldIncludeBenefitInApi(gridBenefits.otherLimitationsType)) {
    transformed.otherLimitations = {
      type: gridBenefits.otherLimitationsType,
    };
    if (
      (gridBenefits.otherLimitationsType === "years" ||
        gridBenefits.otherLimitationsType === "months") &&
      isValidCount(gridBenefits.otherLimitationsAmount)
    ) {
      transformed.otherLimitations.timeframe =
        gridBenefits.otherLimitationsAmount;
    }
  }

  // Extended (non-v5) fields — CD-1940/CD-2067. Sent as a full object
  // (a full-replace PATCH, like the rest of `benefits`) whenever at least one
  // extended field is set, so an edit to any other field on the plan doesn't
  // drop the extended values already on it. Omitted entirely when every
  // extended field is unset, so an edit that never touches these fields
  // doesn't create an empty extended row on the backend.
  const extended = {
    eligibilityHours: gridBenefits.eligibilityHours,
    zeroDayResidual: gridBenefits.zeroDayResidual,
    minimumAmountType: gridBenefits.minimumAmountType,
    minimumAmount: gridBenefits.minimumAmount,
    mandatoryRehabilitation: gridBenefits.mandatoryRehabilitation,
    indexingOfPreDisabilityEarnings:
      gridBenefits.indexingOfPreDisabilityEarnings,
    returnToWorkIncentivePeriod: gridBenefits.returnToWorkIncentivePeriod,
    definitionOfPartialDisability: gridBenefits.definitionOfPartialDisability,
    otherIncomeIntegration: gridBenefits.otherIncomeIntegration,
    socialSecurityIntegration: gridBenefits.socialSecurityIntegration,
    trialWorkDays: gridBenefits.trialWorkDays,
    survivorBenefit: gridBenefits.survivorBenefit,
    worksiteModification: gridBenefits.worksiteModification,
    dayCareBenefits: gridBenefits.dayCareBenefits,
    reasonableAccommodation: gridBenefits.reasonableAccommodation,
    rehabilitativeIncentiveBenefit: gridBenefits.rehabilitativeIncentiveBenefit,
    prudentPerson: gridBenefits.prudentPerson,
    selfReportedLimitations: gridBenefits.selfReportedLimitations,
    recoveryBenefit: gridBenefits.recoveryBenefit,
    residencyRestriction: gridBenefits.residencyRestriction,
    progressiveIllness: gridBenefits.progressiveIllness,
    infectiousContagiousDisease: gridBenefits.infectiousContagiousDisease,
    specificLossAdDAndLossOfSight: gridBenefits.specificLossAdDAndLossOfSight,
    activitiesOfDailyLiving: gridBenefits.activitiesOfDailyLiving,
    maximumCapacity: gridBenefits.maximumCapacity,
    retirementIncomeProtection: gridBenefits.retirementIncomeProtection,
    recurrentDisability: gridBenefits.recurrentDisability,
    portability: gridBenefits.portability,
    eap: gridBenefits.eap,
  };
  if (
    Object.values(extended).some((value) => value !== undefined && value !== "")
  ) {
    transformed.extended = extended;
  }

  // Add objectType and filter to valid fields
  const withObjectType: LtdBenefitApi = {
    ...transformed,
    objectType: "ltd",
  };

  // Filter to only valid LTD fields
  const filtered: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(withObjectType)) {
    if (LTD_VALID_FIELDS.has(key)) {
      filtered[key] = value;
    }
  }

  return cleanupNotDisclosedBenefits<unknown>(filtered) as LtdBenefitApi;
};

/**
 * Transforms LTD benefits from API format to grid format
 *
 * @param apiBenefits - Benefits in nested API format
 * @returns Benefits in flat grid format
 */
export const transformLtdBenefitsFromApi = (
  apiBenefits: Partial<LtdBenefitApi>,
): LtdBenefitGrid => {
  const transformed: Partial<LtdBenefitGrid> = {
    ...apiBenefits,
  };

  // Transform BenefitAmount from API object to grid fields - using builder
  if (
    apiBenefits.benefitAmount &&
    typeof apiBenefits.benefitAmount === "object"
  ) {
    Object.assign(
      transformed,
      transformTypeMultipleAmountsFromApi(
        apiBenefits.benefitAmount,
        "benefitAmount",
        ["amount", "minimum"],
      ),
    );
    delete (transformed as Record<string, unknown>)["benefitAmount"];
  }

  // Transform MaximumAmount from API object to grid fields - using builder
  if (
    apiBenefits.maximumAmount &&
    typeof apiBenefits.maximumAmount === "object"
  ) {
    Object.assign(
      transformed,
      transformTypeMultipleAmountsFromApi(
        apiBenefits.maximumAmount,
        "maximumAmount",
        ["dollarAmount", "percent"],
      ),
    );
    delete (transformed as Record<string, unknown>)["maximumAmount"];
  }

  // Transform GuaranteedIssue from API object to grid fields
  if (
    apiBenefits.guaranteedIssue &&
    typeof apiBenefits.guaranteedIssue === "object"
  ) {
    transformed.guaranteedIssueType = apiBenefits.guaranteedIssue.type;
    transformed.guaranteedIssueAmount = apiBenefits.guaranteedIssue.amount;

    delete (transformed as Record<string, unknown>)["guaranteedIssue"];
  }

  // Transform OwnOccupationPeriod from API object to grid fields
  if (
    apiBenefits.ownOccupationPeriod &&
    typeof apiBenefits.ownOccupationPeriod === "object"
  ) {
    transformed.ownOccupationPeriodType = apiBenefits.ownOccupationPeriod.type;
    transformed.ownOccupationPeriodAmount =
      apiBenefits.ownOccupationPeriod.amount;

    delete (transformed as Record<string, unknown>)["ownOccupationPeriod"];
  }

  // Transform GainfulEarningsTestOwnOccupation from API object to grid fields
  if (
    apiBenefits.gainfulEarningsTestOwnOccupation &&
    typeof apiBenefits.gainfulEarningsTestOwnOccupation === "object"
  ) {
    transformed.gainfulEarningsTestOwnOccupationType =
      apiBenefits.gainfulEarningsTestOwnOccupation.type;
    transformed.gainfulEarningsTestOwnOccupationAmount =
      apiBenefits.gainfulEarningsTestOwnOccupation.amount;

    delete (transformed as Record<string, unknown>)[
      "gainfulEarningsTestOwnOccupation"
    ];
  }

  // Transform GainfulEarningsTestAnyOccupation from API object to grid fields
  if (
    apiBenefits.gainfulEarningsTestAnyOccupation &&
    typeof apiBenefits.gainfulEarningsTestAnyOccupation === "object"
  ) {
    transformed.gainfulEarningsTestAnyOccupationType =
      apiBenefits.gainfulEarningsTestAnyOccupation.type;
    transformed.gainfulEarningsTestAnyOccupationAmount =
      apiBenefits.gainfulEarningsTestAnyOccupation.amount;

    delete (transformed as Record<string, unknown>)[
      "gainfulEarningsTestAnyOccupation"
    ];
  }

  // Transform EliminationPeriod from API object to grid fields
  if (
    apiBenefits.eliminationPeriod &&
    typeof apiBenefits.eliminationPeriod === "object"
  ) {
    transformed.eliminationPeriodType = apiBenefits.eliminationPeriod.type;
    transformed.eliminationPeriodDays = apiBenefits.eliminationPeriod.amount;

    delete (transformed as Record<string, unknown>)["eliminationPeriod"];
  }

  // Transform BenefitDuration from API object to grid fields
  if (
    apiBenefits.benefitDuration &&
    typeof apiBenefits.benefitDuration === "object"
  ) {
    transformed.benefitDurationType = apiBenefits.benefitDuration.type;
    transformed.benefitDurationAmount = apiBenefits.benefitDuration.amount;

    delete (transformed as Record<string, unknown>)["benefitDuration"];
  }

  // Transform DefinitionOfEarnings from API object to grid fields
  if (
    apiBenefits.definitionOfEarnings &&
    typeof apiBenefits.definitionOfEarnings === "object"
  ) {
    transformed.definitionOfEarningsType =
      apiBenefits.definitionOfEarnings.type;
    transformed.definitionOfEarningsAverageMonths =
      apiBenefits.definitionOfEarnings.averageMonth;

    delete (transformed as Record<string, unknown>)["definitionOfEarnings"];
  }

  // Transform MentalIllness from API object to grid fields
  if (
    apiBenefits.mentalIllness &&
    typeof apiBenefits.mentalIllness === "object"
  ) {
    transformed.mentalIllnessType = apiBenefits.mentalIllness.type;
    transformed.mentalIllnessAmount = apiBenefits.mentalIllness.timeframe;

    delete (transformed as Record<string, unknown>)["mentalIllness"];
  }

  // Transform SubstanceAbuse from API object to grid fields
  if (
    apiBenefits.substanceAbuse &&
    typeof apiBenefits.substanceAbuse === "object"
  ) {
    transformed.substanceAbuseType = apiBenefits.substanceAbuse.type;
    transformed.substanceAbuseAmount = apiBenefits.substanceAbuse.timeframe;

    delete (transformed as Record<string, unknown>)["substanceAbuse"];
  }

  // Transform OtherLimitations from API object to grid fields
  if (
    apiBenefits.otherLimitations &&
    typeof apiBenefits.otherLimitations === "object"
  ) {
    transformed.otherLimitationsType = apiBenefits.otherLimitations.type;
    transformed.otherLimitationsAmount = apiBenefits.otherLimitations.timeframe;

    delete (transformed as Record<string, unknown>)["otherLimitations"];
  }

  // Transform PreExistingCondition from API object to grid fields - using builder
  if (
    apiBenefits.preExistingCondition &&
    typeof apiBenefits.preExistingCondition === "object"
  ) {
    Object.assign(
      transformed,
      transformPreExistingConditionFromApi(apiBenefits.preExistingCondition),
    );
    delete (transformed as Record<string, unknown>)["preExistingCondition"];
  }

  // Extended (non-v5) fields — CD-1940/CD-2067
  Object.assign(transformed, transformLtdExtendedFromApi(apiBenefits.extended));
  delete (transformed as Record<string, unknown>)["extended"];

  return transformed as LtdBenefitGrid;
};

type LtdExtendedGridFields = Pick<
  LtdBenefitGrid,
  | "eligibilityHours"
  | "zeroDayResidual"
  | "minimumAmountType"
  | "minimumAmount"
  | "mandatoryRehabilitation"
  | "indexingOfPreDisabilityEarnings"
  | "returnToWorkIncentivePeriod"
  | "definitionOfPartialDisability"
  | "otherIncomeIntegration"
  | "socialSecurityIntegration"
  | "trialWorkDays"
  | "survivorBenefit"
  | "worksiteModification"
  | "dayCareBenefits"
  | "reasonableAccommodation"
  | "rehabilitativeIncentiveBenefit"
  | "prudentPerson"
  | "selfReportedLimitations"
  | "recoveryBenefit"
  | "residencyRestriction"
  | "progressiveIllness"
  | "infectiousContagiousDisease"
  | "specificLossAdDAndLossOfSight"
  | "activitiesOfDailyLiving"
  | "maximumCapacity"
  | "retirementIncomeProtection"
  | "recurrentDisability"
  | "portability"
  | "eap"
>;

const LTD_EXTENDED_FIELD_KEYS: (keyof LtdExtendedGridFields)[] = [
  "eligibilityHours",
  "zeroDayResidual",
  "minimumAmountType",
  "minimumAmount",
  "mandatoryRehabilitation",
  "indexingOfPreDisabilityEarnings",
  "returnToWorkIncentivePeriod",
  "definitionOfPartialDisability",
  "otherIncomeIntegration",
  "socialSecurityIntegration",
  "trialWorkDays",
  "survivorBenefit",
  "worksiteModification",
  "dayCareBenefits",
  "reasonableAccommodation",
  "rehabilitativeIncentiveBenefit",
  "prudentPerson",
  "selfReportedLimitations",
  "recoveryBenefit",
  "residencyRestriction",
  "progressiveIllness",
  "infectiousContagiousDisease",
  "specificLossAdDAndLossOfSight",
  "activitiesOfDailyLiving",
  "maximumCapacity",
  "retirementIncomeProtection",
  "recurrentDisability",
  "portability",
  "eap",
];

// Extended (non-v5) fields — CD-1940/CD-2067. Split out of
// transformLtdBenefitsFromApi to keep that function's complexity down.
// Grid and API field names match 1:1, so a single loop over the shared key
// list replaces one `?? undefined` assignment per field. Every key is
// present (as undefined) even when `extended` itself is absent, so a later
// edit to any other field doesn't leave a stale extended value in the grid
// row from a previous plan.
const transformLtdExtendedFromApi = (
  extended: LtdExtendedBenefitApi | undefined,
): LtdExtendedGridFields =>
  Object.fromEntries(
    LTD_EXTENDED_FIELD_KEYS.map((key) => [key, extended?.[key] ?? undefined]),
  ) as LtdExtendedGridFields;

/** Grid keys that persist under `benefits.extended` — see STD_EXTENDED_GRID_FIELD_KEYS. */
export const LTD_EXTENDED_GRID_FIELD_KEYS: ReadonlySet<string> = new Set(
  LTD_EXTENDED_FIELD_KEYS,
);
